import { Box, Flex, chakra } from '@chakra-ui/react';
import { ChevronLeft } from 'lucide-react';

interface ProfileHeaderProps {
  backgroundImage?: string | null;
  profileImage?: string | null;
  searchBar?: React.ReactNode;
  /** Shown only when viewing someone else -- your own profile is a nav bar tab. */
  onBack?: () => void;
}

export function ProfileHeader({ backgroundImage, profileImage, searchBar, onBack }: ProfileHeaderProps) {
  return (
    <Box
      position="relative"
      // Pull the hero up behind the notch/Dynamic Island and grow its height
      // so the visible portion below the safe area stays 200px tall.
      mt="calc(-1 * env(safe-area-inset-top, 0px))"
      h="calc(200px + env(safe-area-inset-top, 0px))"
    >
      <img
        src={backgroundImage || '/assets/images/volbg.jpg'}
        alt="Cover"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      <Box position="absolute" inset={0} bg="blackAlpha.400" />

      {(searchBar || onBack) && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          zIndex={10}
          // Push the overlaid controls below the notch/Dynamic Island
          pt="calc(1.5rem + env(safe-area-inset-top, 0px))"
          pb={4}
        >
          {/* Back and search share one row so neither can overlap the other on a narrow
              screen, and both clear the notch from the padding above. */}
          <Flex
            maxW={{ base: '100%', md: '920px', xl: '1040px' }}
            mx="auto"
            px={{ base: 5, md: 6 }}
            align="center"
            gap={3}
          >
            {onBack && (
              <chakra.button
                type="button"
                aria-label="Back"
                onClick={onBack}
                flexShrink={0}
                w="40px"
                h="40px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
                border="none"
                cursor="pointer"
                color="volyx.text"
                bg="rgba(255,255,255,0.9)"
                backdropFilter="blur(4px)"
                boxShadow="0 8px 24px rgba(0,0,0,0.12)"
                transition="background-color 0.15s"
                _hover={{ bg: 'white' }}
              >
                <ChevronLeft size={24} />
              </chakra.button>
            )}
            {searchBar && <Box flex="1" minW={0}>{searchBar}</Box>}
          </Flex>
        </Box>
      )}

      <Box
        position="absolute"
        bottom="-60px"
        left="50%"
        ml="-60px"
        w="120px"
        h="120px"
        borderRadius="60px"
        bg="white"
        p="4px"
        boxShadow="md"
      >
        <img
          src={profileImage || '/assets/images/blank-profile.png'}
          alt="Profile"
          style={{ width: '100%', height: '100%', borderRadius: '56px', objectFit: 'cover' }}
        />
      </Box>
    </Box>
  );
}
